import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { DrawerProps } from 'app/types/drawer';
import { Global } from 'constants/index';

type TableSortsType = Record<string, { columnKey: string; direction: 'ascend' | 'descend' } | undefined>;

type GlobalSliceType = {
  language: string,
  drawer: DrawerProps | null,
  tableSorts: TableSortsType;
}

const getInitialState = (): GlobalSliceType => {
  const language = localStorage.getItem(Global.LANG_KEY) || 'en';

  let tableSorts: TableSortsType = {};
  try {
    const raw = localStorage.getItem(Global.TABLE_SORTS_KEY || 'tableSorts');
    if (raw) tableSorts = JSON.parse(raw);
  } catch {
    tableSorts = {};
  }

  return {
    language,
    drawer: null,
    tableSorts,
  };
};

export const globalSlice = createSlice({
  name: 'global',
  initialState: getInitialState(),
  reducers: {
    setLanguage: (state, { payload }: PayloadAction<string>) => {
      state.language = payload;
      localStorage.setItem(Global.LANG_KEY, payload);
    },
    openDrawer: (state, { payload }: PayloadAction<DrawerProps>) => {
      state.drawer = payload;
    },
    closeDrawer: (state) => {
      state.drawer = null;
    },
    setDrawerLoading: (state, { payload }: PayloadAction<boolean>) => {
      if (state.drawer) state.drawer.isLoading = payload;
    },
    setTableSort: (state, { payload }: PayloadAction<{ tableKey: string; columnKey: string; direction: 'ascend' | 'descend' }>
    ) => {
      state.tableSorts[payload.tableKey] = {
        columnKey: payload.columnKey,
        direction: payload.direction,
      };
      localStorage.setItem(Global.TABLE_SORTS_KEY, JSON.stringify(state.tableSorts));
    },
    clearTableSort: (state, { payload }: PayloadAction<{ tableKey: string }>) => {
      delete state.tableSorts[payload.tableKey];
      localStorage.setItem(Global.TABLE_SORTS_KEY, JSON.stringify(state.tableSorts));
    },
  }
});

export const globalActions = globalSlice.actions;

export const selectors = {
  getLanguage: (state: RootState) => state.global.language,
  getDrawer: (state: RootState) => state.global.drawer,
  getTableSort:
    (tableKey?: string) => (state: RootState) => {
      if (!tableKey) return null;
      return state.global.tableSorts[tableKey];
    },
};